import { useState } from "react";
import axios from "axios";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, Eyebrow, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { API } from "@/lib/api";

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      await axios.post(`${API}/launch-signups`, { email });
      setState("done");
    } catch (err) {
      setState("idle");
      setError("Something went wrong — please try again.");
    }
  };

  return (
    <div data-testid="coming-soon-page" className="pt-20">
      <section className="min-h-[85vh] flex items-center">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 w-full py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Reveal delay={0.1} y={16}>
              <Eyebrow className="mb-8">Coming soon — Capstone In-House Brand</Eyebrow>
            </Reveal>
            <h1 className="font-serif text-[12vw] sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.05] tracking-tight" data-testid="coming-soon-page-headline">
              <MaskedLine delay={0.25}>Beyond</MaskedLine>
              <MaskedLine delay={0.4}>
                <span className="italic text-green">hats.</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.6} className="mt-10 max-w-xl space-y-6">
              <p className="text-smoke leading-relaxed">
                We're building our own clothing brand — designed, cut and sewn
                in our UK factory. Outerwear, shirting, and much more, made
                with the same honest standards and the same skilled hands you
                already know from our hats.
              </p>
              <p className="text-smoke leading-relaxed">
                No noise, no countdown gimmicks. Leave your email and we'll
                write to you once, the day the first collection is ready.
              </p>
            </Reveal>

            <Reveal delay={0.75} className="mt-12 max-w-xl">
              {state === "done" ? (
                <div className="border border-line bg-cream p-10" data-testid="launch-success">
                  <span className="inline-flex items-center justify-center w-10 h-10 border border-green text-green mb-6">
                    <Check size={18} strokeWidth={1.5} />
                  </span>
                  <p className="eyebrow text-honey mb-3">You're on the list</p>
                  <p className="font-serif text-2xl md:text-3xl tracking-tight">Thank you.</p>
                  <p className="mt-4 text-smoke text-sm leading-relaxed">
                    One email, the day we launch. Nothing more — a handshake
                    counts more than a newsletter here.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="launch-signup-form">
                  <label className="eyebrow !text-[0.6rem] text-smoke block mb-1">
                    Your email
                  </label>
                  <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
                    <input
                      data-testid="launch-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.co.uk"
                      className="field flex-1"
                    />
                    <button
                      data-testid="launch-submit-button"
                      type="submit"
                      disabled={state === "sending"}
                      className="group eyebrow !text-[0.625rem] bg-green text-ivory px-8 py-4 hover:bg-ink transition-colors duration-300 disabled:opacity-50 inline-flex items-center gap-2 shrink-0"
                    >
                      {state === "sending" ? "Joining…" : "Be first to know"}
                      <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                  </div>
                  {error && (
                    <p data-testid="launch-error" className="mt-4 text-sm text-honey italic font-serif">{error}</p>
                  )}
                  <p className="eyebrow !text-[0.6rem] text-smoke mt-6">
                    One email at launch. Nothing more.
                  </p>
                </form>
              )}
            </Reveal>
          </div>

          <div className="lg:col-span-4 hidden lg:block">
            <Reveal delay={0.5}>
              <div className="border border-line">
                <img
                  src="/images/factory-cloth.jpg"
                  alt="Tartan, at home"
                  data-testid="coming-soon-cloth-image"
                  className="w-full aspect-[3/4] object-cover"
                />
              </div>
              <p className="eyebrow !text-[0.6rem] text-smoke mt-4">
                The mood we're making
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36 grid md:grid-cols-3 gap-px bg-line border border-line">
        {[
          { n: "01", t: "Cut here", b: "Every pattern drafted and cut under our own roof — no outsourced shortcuts." },
          { n: "02", t: "Sewn here", b: "The same machinists who sew your hats will sew the clothing line." },
          { n: "03", t: "Finished properly", b: "If it doesn't meet the standard, it doesn't leave the factory." },
        ].map((c, i) => (
          <div key={c.n} className="bg-ivory p-10 md:p-14" data-testid={`coming-soon-pillar-${c.n}`}>
            <Reveal delay={i * 0.1}>
              <p className="font-serif text-3xl text-honey">{c.n}</p>
              <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-4">{c.t}</h2>
              <p className="text-smoke text-sm leading-relaxed mt-4">{c.b}</p>
            </Reveal>
          </div>
        ))}
      </section>
    </div>
  );
}
